import Image from "next/image";
import Link from "next/link";
import { resolveMediaUrl } from "@/lib/media";
import type { AttractionList } from "@/lib/dto";
import { ArrowRight, MapPin } from "lucide-react";
import { Button } from "../ui/Button";

/** `attraction.data.abstract` must already be sanitized on the server (see
 *  `withSanitizedAbstracts`) — this card renders inside a client list, and the
 *  sanitizer (DOMPurify + jsdom) must never ship to the browser. */
type Props = { attraction: AttractionList; href: string };

/** Attraction/place card — image tile + name below, matching production's
 *  PlacesCard exactly (website/src/components/Card/PlacesCard.jsx). */
export function PlacesCard({ attraction, href }: Props) {
  const img = attraction.data.thumbnail?.url
    ? resolveMediaUrl(attraction.data.thumbnail.url)
    : null;

  return (
    <div className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100">
      <div className="relative image-slot aspect-[4/5] overflow-hidden">
        {img ? (
          <Image
            src={img}
            alt={attraction.data.thumbnail?.alt || attraction.title}
            fill
            sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 90vw"
            className="object-cover transition-transform duration-300 group-hover:scale-110"
          />
        ) : null}
        <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
          <span className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full bg-white/95 backdrop-blur-sm text-[10px] font-black uppercase tracking-widest text-primary shadow-sm">
            {attraction.data.package?.length} Packages
          </span>
        </div>
        <div className="absolute z-10 inset-x-0 bottom-0 bg-gradient-to-t from-headings via-headings/60 to-transparent pt-20 pb-6 px-6">
          <h3 className="text-xl md:text-2xl font-bold text-white leading-tight">
            {attraction.title}
          </h3>
        </div>
        <div className="absolute inset-0 bg-primary/90 opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col justify-center items-center px-8 text-center backdrop-blur-sm z-20">
          <MapPin aria-hidden className="text-white" size={32} />
          <h4 className="text-white text-xl font-black my-3">
            {attraction.title}
          </h4>
          <div
            dangerouslySetInnerHTML={{
              __html: attraction.data.abstract ?? "",
            }}
            className="text-white/90 text-md leading-relaxed mb-6 line-clamp-4"
          />
          <Button
            size="sm"
            className="inline-flex items-center gap-2 bg-white text-primary rounded-full text-xxs font-black uppercase tracking-widest shadow-lg hover:bg-light transition-colors"
            href={href}
          >
            Explore Journey <ArrowRight size={14} aria-hidden />
          </Button>
        </div>
      </div>
    </div>
  );
}
